import { useEffect, useRef, useState } from "react";
import { AiBlock } from "@/components/ax/AiBlock";
import { RefChip } from "@/components/ax/RefChip";
import { useI18n } from "@/i18n";
import { TriggerChecklist } from "./TriggerChecklist";

const SECTIONS = ["framework", "assumptions", "unknowns", "tradeoffs", "recommendation", "change"];

function useActiveSection(keys) {
  const [active, setActive] = useState(keys[0]);
  const lock = useRef(0);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && Date.now() > lock.current && setActive(e.target.dataset.section)),
      { rootMargin: "-15% 0px -65% 0px" },
    );
    keys.forEach((k) => {
      const el = document.getElementById(`brief-${k}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [keys.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = (key) => {
    lock.current = Date.now() + 1000;
    setActive(key);
  };
  return [active, pick];
}

const goTo = (key) => {
  const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  document.getElementById(`brief-${key}`)?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
};

const Node = ({ on }) => (
  <span
    aria-hidden="true"
    className={`block h-2 w-2 shrink-0 rounded-full border-2 ${on ? "border-ax-accent bg-ax-accent" : "border-ax-border bg-ax-bg"}`}
    style={{ width: 10, height: 10 }}
  />
);

export const Brief = ({ brief, items, options, onTriggerChange, triggerDisabled, triggerError }) => {
  const { t } = useI18n();
  const item = (id) => items.find((i) => i.id === id);
  const option = (id) => options.find((o) => o.id === id);
  const rec = brief.recommendation;
  const recOption = option(rec.optionId);
  const has = {
    framework: !!brief.framework,
    assumptions: brief.assumptions.some((a) => item(a.itemId)),
    unknowns: brief.unknowns.some((u) => item(u.itemId)),
    tradeoffs: brief.tradeoffs.some((x) => option(x.optionId)),
    recommendation: !!recOption,
    change: !!brief.whatWouldChange,
  };
  const keys = SECTIONS.filter((k) => has[k]);
  const [active, pick] = useActiveSection(keys);

  const sections = {
    framework: <p className="m-0 text-base leading-6 text-ax-text">{brief.framework}</p>,
    assumptions: (
      <ul className="m-0 flex list-none flex-col gap-4 p-0">
        {brief.assumptions
          .filter((a) => item(a.itemId))
          .map((a) => (
            <li key={a.itemId} data-testid={`assumption-${a.itemId}`}>
              <div className="flex items-start gap-3">
                <RefChip item={item(a.itemId)} />
                <span className="text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{item(a.itemId).text}</span>
              </div>
              <p className="m-0 mt-1 pl-0 text-sm leading-5 text-ax-text-2">{a.challenge}</p>
            </li>
          ))}
      </ul>
    ),
    unknowns: (
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {brief.unknowns
          .filter((u) => item(u.itemId))
          .map((u) => (
            <li key={u.itemId} className="flex items-start gap-3" data-testid={`unknown-${u.itemId}`}>
              <RefChip item={item(u.itemId)} />
              <span className="text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{item(u.itemId).text}</span>
            </li>
          ))}
      </ul>
    ),
    tradeoffs: (
      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {brief.tradeoffs
          .filter((x) => option(x.optionId))
          .map((x) => (
            <li key={x.optionId} data-testid={`tradeoff-${x.optionId}`}>
              <span className="text-base font-medium leading-6 text-ax-text">{option(x.optionId).name}</span>
              <p className="m-0 text-sm leading-5 text-ax-text-2">{x.line}</p>
            </li>
          ))}
      </ul>
    ),
    recommendation: recOption && (
      <>
        <p className="m-0 text-base font-semibold leading-6 text-ax-text" data-testid="recommendation-option">
          {recOption.name}
        </p>
        <p className="m-0 mt-1 text-base leading-6 text-ax-text">{rec.because}</p>
        <p className="m-0 mt-3 font-mono text-[13px] leading-[18px] text-ax-text-2" data-testid="recommendation-confidence">
          {t("brief.confidence")} {rec.confidence.toFixed(2)}
        </p>
      </>
    ),
    change: <>
      <p className="m-0 text-base leading-6 text-ax-text">{brief.whatWouldChange}</p>
      <TriggerChecklist brief={brief} onChange={onTriggerChange} disabled={triggerDisabled} error={triggerError} />
    </>,
  };

  return (
    <div data-testid="brief" className="lg:grid lg:grid-cols-[160px_minmax(0,1fr)] lg:gap-6">
      <nav
        aria-label={t("brief.toc")}
        data-testid="brief-axis-rail"
        className="sticky top-8 hidden self-start lg:block"
      >
        <ol className="relative m-0 list-none p-0">
          <span aria-hidden="true" className="absolute bottom-3 left-[4px] top-3 w-[2px] bg-ax-border" />
          {keys.map((k) => (
            <li key={k}>
              <button
                type="button"
                onClick={() => {
                  pick(k);
                  goTo(k);
                }}
                aria-current={active === k ? "true" : undefined}
                data-testid={`rail-${k}`}
                className={`relative flex min-h-[44px] w-full items-center gap-3 text-left text-sm leading-5 ${
                  active === k ? "font-medium text-ax-link" : "text-ax-muted"
                }`}
              >
                <Node on={active === k} />
                {t(`brief.${k}`)}
              </button>
            </li>
          ))}
        </ol>
      </nav>

      <ol className="m-0 list-none p-0">
        {keys.map((k, i) => (
          <li
            key={k}
            id={`brief-${k}`}
            data-section={k}
            data-testid={`brief-section-${k}`}
            className="relative scroll-mt-20 pb-4 pl-7 lg:scroll-mt-6 lg:pl-0"
          >
            <span
              aria-hidden="true"
              className={`absolute left-[4px] w-[2px] bg-ax-border lg:hidden ${i === 0 ? "top-6" : "top-0"} ${i === keys.length - 1 ? "h-6" : "bottom-0"}`}
            />
            <span aria-hidden="true" className="absolute left-0 top-5 lg:hidden">
              <Node on={active === k} />
            </span>
            <AiBlock title={t(`brief.${k}`)}>{sections[k]}</AiBlock>
          </li>
        ))}
      </ol>
    </div>
  );
};

export const BriefLoading = () => {
  const { t } = useI18n();
  return (
    <div data-testid="brief-loading" aria-busy="true" className="flex gap-4">
      <div aria-hidden="true" className="relative w-4 shrink-0">
        <span className="absolute bottom-3 left-[7px] top-3 w-[2px] bg-ax-border" />
        <div className="relative flex h-full min-h-[160px] flex-col justify-between py-1">
          {SECTIONS.map((k) => (
            <Node key={k} on={false} />
          ))}
        </div>
      </div>
      <p className="m-0 text-base leading-6 text-ax-text-2">{t("brief.composing")}</p>
    </div>
  );
};
